import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [track]
 * @property {HTMLButtonElement} [btnPrev]
 * @property {HTMLButtonElement} [btnNext]
 * @property {HTMLButtonElement[]} [dots]
 *
 * @extends {Component<Refs>}
 */
class PlpTextSlider extends Component {
  #index = 0;
  #touchStartX = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('touchstart', this.#onTouchStart);
    this.removeEventListener('touchend', this.#onTouchEnd);
  }

  prev() {
    this.#go(this.#index - 1);
  }

  next() {
    this.#go(this.#index + 1);
  }

  /** @param {string} index */
  goTo(index) {
    const i = parseInt(index, 10);
    if (!Number.isNaN(i)) this.#go(i);
  }

  /** @param {TouchEvent} event */
  #onTouchStart = (event) => {
    this.#touchStartX = event.touches[0].clientX;
  };

  /** @param {TouchEvent} event */
  #onTouchEnd = (event) => {
    const delta = event.changedTouches[0].clientX - this.#touchStartX;
    if (Math.abs(delta) < 40) return;
    this.#go(delta < 0 ? this.#index + 1 : this.#index - 1);
  };

  /** @param {number} index */
  #go(index) {
    const slides = this.refs.slides ?? [];
    this.#index = Math.max(0, Math.min(index, slides.length - 1));
    this.#update();
  }

  #update() {
    const slides = this.refs.slides ?? [];
    const track = this.refs.track;
    const dots = this.refs.dots ?? [];

    if (!track || !slides.length) return;

    track.style.setProperty('--pts-offset', `${this.#index * -100}%`);

    if (this.refs.btnPrev) this.refs.btnPrev.disabled = this.#index <= 0;
    if (this.refs.btnNext) this.refs.btnNext.disabled = this.#index >= slides.length - 1;

    for (const [i, dot] of dots.entries()) {
      dot.setAttribute('aria-current', String(i === this.#index));
    }
  }
}

if (!customElements.get('custom-plp-text-slider')) {
  customElements.define('custom-plp-text-slider', PlpTextSlider);
}
